import React, { useState } from 'react';
import { CheckCircleIcon, Edit3Icon, ShieldCheckIcon, FileTextIcon, ImageIcon, FileIcon, DownloadIcon, EyeIcon, TrashIcon, UploadIcon, CreditCardIcon, AlertTriangleIcon, ClockIcon, CalendarIcon, UsersIcon } from 'lucide-react';
import { InvoiceDetails, InvoiceDocument, formatAmount } from './documentReviewTypes';
import { VendorSelector } from '../vendor/VendorSelector';
import { allVendors } from '../vendor/sharedVendorData';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
interface DocumentDataPanelProps {
  details: InvoiceDetails;
  document: InvoiceDocument;
  isEditAllMode?: boolean;
  onUpdateDocument?: (updates: Partial<InvoiceDocument>) => void;
}
// Mock historical notes
const mockPreviousNotes = [{
  id: 'note-1',
  author: 'Sarah Chen',
  date: '15 Jan 2024 2:30 PM',
  visibility: 'Internal' as NoteVisibility,
  text: 'Verified line items against PO. All quantities match.'
}, {
  id: 'note-2',
  author: 'Mike Johnson',
  date: '14 Jan 2024 10:15 AM',
  visibility: 'Internal' as NoteVisibility,
  text: 'Received from vendor via email. Forwarded to AP team.'
}];
// Mock attachments data
const mockAttachments = [{
  id: 'att-1',
  name: 'Original_Invoice.pdf',
  type: 'pdf',
  size: 1250000,
  uploadDate: '2024-01-15',
  uploadedBy: 'System (Email)',
  status: 'Active'
}, {
  id: 'att-2',
  name: 'PO_Confirmation.pdf',
  type: 'pdf',
  size: 890000,
  uploadDate: '2024-01-10',
  uploadedBy: 'Sarah Chen',
  status: 'Active'
}, {
  id: 'att-3',
  name: 'Delivery_Receipt.jpg',
  type: 'jpg',
  size: 2340000,
  uploadDate: '2024-01-14',
  uploadedBy: 'Warehouse Team',
  status: 'Active'
}];
// Helper functions
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
function getFileIcon(type: string) {
  switch (type.toLowerCase()) {
    case 'pdf':
      return <FileTextIcon className="w-5 h-5 text-red-500" data-id="element-4507" />;
    case 'docx':
    case 'doc':
      return <FileTextIcon className="w-5 h-5 text-blue-500" data-id="element-4508" />;
    case 'xlsx':
    case 'xls':
      return <FileTextIcon className="w-5 h-5 text-green-500" data-id="element-4509" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return <ImageIcon className="w-5 h-5 text-purple-500" data-id="element-4510" />;
    default:
      return <FileIcon className="w-5 h-5 text-gray-500" data-id="element-4511" />;
  }
}
function getVisibilityIcon(visibility: NoteVisibility) {
  switch (visibility) {
    case 'Public':
      return <span className="inline-flex items-center text-green-600" data-id="element-4512">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4513" />
          Public
        </span>;
    case 'Internal':
      return <span className="inline-flex items-center text-blue-600" data-id="element-4514">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4515" />
          Internal
        </span>;
    case 'Creator-only':
      return <span className="inline-flex items-center text-red-600" data-id="element-4516">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4517" />
          Private
        </span>;
    default:
      return <span className="inline-flex items-center text-gray-600" data-id="element-4518">
          <UsersIcon className="w-3 h-3 mr-1" data-id="element-4519" />
          {visibility}
        </span>;
  }
}
function getComplianceStatusBadge(status: string) {
  switch (status) {
    case 'Compliant':
    case 'Verified':
    case 'Valid':
    case 'Clear':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-4520">
          <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-4521" />
          {status}
        </span>;
    case 'Pending Review':
    case 'Not Verified':
    case 'Pending':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-4522">
          <ClockIcon className="w-3 h-3 mr-1" data-id="element-4523" />
          {status}
        </span>;
    case 'Non-Compliant':
    case 'Failed':
    case 'Expired':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-4524">
          <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-4525" />
          {status}
        </span>;
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4526">
          {status}
        </span>;
  }
}
export function DocumentDataPanel({
  details,
  document,
  isEditAllMode = false,
  onUpdateDocument
}: DocumentDataPanelProps) {
  const [activeTab, setActiveTab] = useState('details');
  // Notes state
  const [noteText, setNoteText] = useState(document.notes || '');
  const [noteVisibility, setNoteVisibility] = useState<NoteVisibility>('Internal');
  // Find current vendor ID based on supplier name
  const currentVendor = allVendors.find(v => v.name === document.supplier);
  const currentVendorId = currentVendor ? currentVendor.id : null;
  const handleDocumentChange = (field: keyof InvoiceDocument, value: any) => {
    if (onUpdateDocument) {
      onUpdateDocument({
        [field]: value
      });
    }
  };
  const handleNumberChange = (field: keyof InvoiceDocument, value: string) => {
    if (onUpdateDocument) {
      const numValue = parseFloat(value);
      onUpdateDocument({
        [field]: isNaN(numValue) ? 0 : numValue
      });
    }
  };
  const handleVendorChange = (vendorId: string | null) => {
    if (vendorId && onUpdateDocument) {
      const vendor = allVendors.find(v => v.id === vendorId);
      if (vendor) {
        onUpdateDocument({
          supplier: vendor.name,
          supplierABN: vendor.taxId || ''
        });
      }
    }
  };
  // Build vendor verification data
  const vendorVerification = {
    complianceStatus: details.vendorBankCheck ? 'Compliant' : 'Pending Review',
    bankVerification: {
      status: details.vendorBankCheck ? 'Verified' : 'Not Verified',
      lastChecked: details.lastVerifiedDate || null,
      accountName: details.vendorName,
      issues: details.vendorBankCheck ? [] : [{
        severity: 'High',
        description: 'Bank account details not yet verified'
      }]
    },
    documents: [{
      id: 1,
      name: 'Business Registration',
      type: 'Legal',
      uploadDate: '2023-06-15',
      expiryDate: null,
      status: 'Valid'
    }, {
      id: 2,
      name: 'Insurance Certificate',
      type: 'Insurance',
      uploadDate: '2023-06-15',
      expiryDate: '2024-06-15',
      status: 'Valid'
    }, {
      id: 3,
      name: 'Tax Compliance Certificate',
      type: 'Tax',
      uploadDate: '2023-09-01',
      expiryDate: '2024-09-01',
      status: 'Valid'
    }],
    sanctionsCheck: {
      status: 'Clear',
      lastChecked: details.lastVerifiedDate || 'N/A',
      issues: []
    }
  };
  const tabs = [{
    id: 'details',
    label: 'Details'
  }, {
    id: 'notes',
    label: 'Notes'
  }, {
    id: 'attachments',
    label: 'Attachments'
  }, {
    id: 'verification',
    label: 'Vendor Verification'
  }];
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-full" data-id="element-4527">
      <div className="px-6 pt-4 border-b border-gray-200 flex-shrink-0" data-id="element-4528">
        <div className="flex gap-1" data-id="element-4529">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === tab.id ? 'bg-white border border-b-white border-gray-200 text-indigo-700 -mb-px' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4530">
              {tab.label}
            </button>)}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6 min-h-0" data-id="element-4531">
        {activeTab === 'details' && <div className="space-y-4" data-id="element-4532">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-4533">
              <div className="w-full max-w-md" data-id="element-4534">
                <div className="text-xs text-gray-500 mb-1" data-id="element-4535">Vendor</div>
                {isEditAllMode ? <VendorSelector value={currentVendorId} onChange={handleVendorChange} placeholder="Search and select vendor..." data-id="element-4536" /> : <div className="text-lg font-semibold text-gray-900" data-id="element-4537">
                    {details.vendorName}
                  </div>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-x-6 gap-y-4" data-id="element-4538">
              <div className="space-y-3" data-id="element-4539">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4540">
                  Vendor
                </div>
                <div data-id="element-4541">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4542">
                    Supplier Code:
                  </label>
                  <div className="text-sm" data-id="element-4543">{details.supplierCode}</div>
                </div>
                <div data-id="element-4544">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4545">
                    Tax Number:
                  </label>
                  <div className="text-sm" data-id="element-4546">{details.vendorTaxNo}</div>
                </div>
                <div data-id="element-4547">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4548">
                    Email:
                  </label>
                  <div className="text-sm" data-id="element-4549">{details.email}</div>
                </div>
                <div data-id="element-4550">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4551">
                    Phone:
                  </label>
                  <div className="text-sm" data-id="element-4552">{details.phoneNumber || '-'}</div>
                </div>
                {details.vendorBankCheck !== undefined && <div data-id="element-4553">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4554">
                      Bank Check:
                    </label>
                    <div className="flex items-center" data-id="element-4555">
                      {details.vendorBankCheck ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-4556" /> : <span className="text-sm text-gray-400" data-id="element-4557">
                          Not verified
                        </span>}
                    </div>
                  </div>}
              </div>
              <div className="space-y-3" data-id="element-4558">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4559">
                  Entity
                </div>
                <div data-id="element-4560">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4561">
                    Entity:
                  </label>
                  <div className="text-sm" data-id="element-4562">{details.entity}</div>
                </div>
                <div data-id="element-4563">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4564">
                    Site:
                  </label>
                  <div className="text-sm" data-id="element-4565">{details.site}</div>
                </div>
                {details.paymentTerms && <div data-id="element-4566">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4567">
                      Payment Terms:
                    </label>
                    <div className="text-sm" data-id="element-4568">{details.paymentTerms}</div>
                  </div>}
              </div>
              <div className="space-y-3" data-id="element-4569">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4570">
                  Address
                </div>
                <div data-id="element-4571">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4572">
                    Address:
                  </label>
                  <div className="text-sm" data-id="element-4573">{details.address}</div>
                </div>
                <div data-id="element-4574">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4575">
                    City:
                  </label>
                  <div className="text-sm" data-id="element-4576">{details.city}</div>
                </div>
                <div data-id="element-4577">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4578">
                    State:
                  </label>
                  <div className="text-sm" data-id="element-4579">{details.state}</div>
                </div>
                <div data-id="element-4580">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4581">
                    Postcode:
                  </label>
                  <div className="text-sm" data-id="element-4582">{details.postcode}</div>
                </div>
                <div data-id="element-4583">
                  <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4584">
                    Country:
                  </label>
                  <div className="text-sm" data-id="element-4585">{details.country}</div>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200" data-id="element-4586">
              <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-4587">
                Document Header
              </h3>
              <div className="grid grid-cols-3 gap-x-6 gap-y-4" data-id="element-4588">
                <div className="space-y-3" data-id="element-4589">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4590">
                    Document
                  </div>
                  <div data-id="element-4591">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4592">
                      Number:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.invoiceNumber} onChange={e => handleDocumentChange('invoiceNumber', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4593" /> : <div className="text-sm" data-id="element-4594">{document.invoiceNumber}</div>}
                  </div>
                  <div data-id="element-4595">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4596">
                      Currency:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.currency} onChange={e => handleDocumentChange('currency', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4597" /> : <div className="text-sm" data-id="element-4598">{document.currency}</div>}
                  </div>
                  {(document.poNumber || isEditAllMode) && <div data-id="element-4599">
                      <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4600">
                        PO Number:
                      </label>
                      {isEditAllMode ? <input type="text" value={document.poNumber || ''} onChange={e => handleDocumentChange('poNumber', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Optional" data-id="element-4601" /> : <div className="text-sm font-medium text-blue-600" data-id="element-4602">
                          {document.poNumber}
                        </div>}
                    </div>}
                </div>
                <div className="space-y-3" data-id="element-4603">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4604">
                    Dates
                  </div>
                  <div data-id="element-4605">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4606">
                      Document Date:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.invoiceDate} onChange={e => handleDocumentChange('invoiceDate', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4607" /> : <div className="text-sm" data-id="element-4608">{document.invoiceDate}</div>}
                  </div>
                  <div data-id="element-4609">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4610">
                      Due Date:
                    </label>
                    {isEditAllMode ? <input type="text" value={document.dueDate} onChange={e => handleDocumentChange('dueDate', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4611" /> : <div className="text-sm" data-id="element-4612">{document.dueDate}</div>}
                  </div>
                  {(document.receivedDate || isEditAllMode) && <div data-id="element-4613">
                      <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4614">
                        Received:
                      </label>
                      {isEditAllMode ? <input type="text" value={document.receivedDate || ''} onChange={e => handleDocumentChange('receivedDate', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Optional" data-id="element-4615" /> : <div className="text-sm" data-id="element-4616">{document.receivedDate}</div>}
                    </div>}
                </div>
                <div className="space-y-3" data-id="element-4617">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-4618">
                    Amounts
                  </div>
                  <div data-id="element-4619">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4620">
                      Subtotal:
                    </label>
                    {isEditAllMode ? <input type="number" step="0.01" value={document.subtotal} onChange={e => handleNumberChange('subtotal', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4621" /> : <div className="text-sm" data-id="element-4622">
                        {formatAmount(document.subtotal, document.currency)}
                      </div>}
                  </div>
                  <div data-id="element-4623">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4624">
                      Tax:
                    </label>
                    {isEditAllMode ? <input type="number" step="0.01" value={document.taxAmt} onChange={e => handleNumberChange('taxAmt', e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4625" /> : <div className="text-sm" data-id="element-4626">
                        {formatAmount(document.taxAmt, document.currency)}
                      </div>}
                  </div>
                  <div data-id="element-4627">
                    <label className="block text-xs text-gray-600 mb-0.5" data-id="element-4628">
                      Total:
                    </label>
                    {isEditAllMode ? <input type="number" step="0.01" value={document.total} onChange={e => handleNumberChange('total', e.target.value)} className="w-full px-2 py-1 text-sm font-semibold border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-4629" /> : <div className="text-sm font-semibold" data-id="element-4630">
                        {formatAmount(document.total, document.currency)}
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* NOTES TAB */}
        {activeTab === 'notes' && <div className="space-y-6" data-id="element-4631">
            <StandardizedNotes value={noteText} onChange={setNoteText} visibility={noteVisibility} onVisibilityChange={setNoteVisibility} label="Invoice Notes" placeholder="Add notes about this document..." rows={4} data-id="element-4632" />

            {/* Previous Notes */}
            <div data-id="element-4633">
              <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-4634">
                Previous Notes
              </h4>
              <div className="space-y-3" data-id="element-4635">
                {mockPreviousNotes.map(note => <div key={note.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-4636">
                    <div className="flex items-center justify-between mb-2" data-id="element-4637">
                      <div className="flex items-center gap-2" data-id="element-4638">
                        <span className="text-sm font-medium text-gray-900" data-id="element-4639">
                          {note.author}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-4640">
                          {note.date}
                        </span>
                      </div>
                      <span className="text-xs" data-id="element-4641">
                        {getVisibilityIcon(note.visibility)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-4642">{note.text}</p>
                  </div>)}
              </div>
            </div>
          </div>}

        {/* ATTACHMENTS TAB */}
        {activeTab === 'attachments' && <div className="space-y-4" data-id="element-4643">
            <div className="flex items-center justify-between mb-2" data-id="element-4644">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-4645">
                Document Attachments
              </h4>
              <span className="text-xs text-gray-500" data-id="element-4646">
                {mockAttachments.length} files
              </span>
            </div>

            {/* Attachment List */}
            <div className="space-y-2" data-id="element-4647">
              {mockAttachments.map(attachment => <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-4648">
                  <div className="flex items-center gap-3" data-id="element-4649">
                    {getFileIcon(attachment.type)}
                    <div data-id="element-4650">
                      <div className="text-sm font-medium text-gray-900" data-id="element-4651">
                        {attachment.name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-4652">
                        <span data-id="element-4653">{formatFileSize(attachment.size)}</span>
                        <span data-id="element-4654">•</span>
                        <span data-id="element-4655">{attachment.uploadedBy}</span>
                        <span data-id="element-4656">•</span>
                        <span data-id="element-4657">{attachment.uploadDate}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-4658">
                    <button className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors" title="Preview" data-id="element-4659">
                      <EyeIcon className="w-4 h-4" data-id="element-4660" />
                    </button>
                    <button className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors" title="Download" data-id="element-4661">
                      <DownloadIcon className="w-4 h-4" data-id="element-4662" />
                    </button>
                    <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete" data-id="element-4663">
                      <TrashIcon className="w-4 h-4" data-id="element-4664" />
                    </button>
                  </div>
                </div>)}
            </div>

            {/* Upload Area */}
            <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer" data-id="element-4665">
              <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-4666" />
              <p className="text-sm text-gray-600" data-id="element-4667">
                Drop files here or click to upload
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-4668">
                PDF, DOC, XLS, JPG up to 10MB
              </p>
            </div>
          </div>}

        {/* VENDOR VERIFICATION TAB */}
        {activeTab === 'verification' && <div className="space-y-4" data-id="element-4669">
            {/* Overall Compliance Status */}
            <div className={`rounded-lg p-4 flex items-start gap-3 ${vendorVerification.complianceStatus === 'Compliant' ? 'bg-green-50 border border-green-200' : vendorVerification.complianceStatus === 'Pending Review' ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`} data-id="element-4670">
              {vendorVerification.complianceStatus === 'Compliant' ? <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-4671" /> : vendorVerification.complianceStatus === 'Pending Review' ? <ClockIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-4672" /> : <AlertTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-4673" />}
              <div data-id="element-4674">
                <div className={`text-sm font-semibold mb-1 ${vendorVerification.complianceStatus === 'Compliant' ? 'text-green-900' : vendorVerification.complianceStatus === 'Pending Review' ? 'text-yellow-900' : 'text-red-900'}`} data-id="element-4675">
                  Vendor {vendorVerification.complianceStatus}
                </div>
                <div className={`text-xs ${vendorVerification.complianceStatus === 'Compliant' ? 'text-green-700' : vendorVerification.complianceStatus === 'Pending Review' ? 'text-yellow-700' : 'text-red-700'}`} data-id="element-4676">
                  Last verified: {details.lastVerifiedDate || 'N/A'}
                </div>
              </div>
            </div>

            {/* Bank Account Verification */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4677">
              <div className="flex items-center justify-between mb-3" data-id="element-4678">
                <div className="flex items-center gap-2" data-id="element-4679">
                  <CreditCardIcon className="w-4 h-4 text-gray-500" data-id="element-4680" />
                  <span className="text-sm font-semibold text-gray-900" data-id="element-4681">
                    Bank Account Verification
                  </span>
                </div>
                {getComplianceStatusBadge(vendorVerification.bankVerification.status)}
              </div>
              {vendorVerification.bankVerification.lastChecked && <p className="text-xs text-gray-500 mb-2" data-id="element-4682">
                  Last checked:{' '}
                  {vendorVerification.bankVerification.lastChecked}
                </p>}
              {vendorVerification.bankVerification.issues.length > 0 && <div className="mt-2 space-y-1" data-id="element-4683">
                  {vendorVerification.bankVerification.issues.map((issue, idx) => <div key={idx} className={`text-xs flex items-start gap-1 ${issue.severity === 'High' ? 'text-red-600' : 'text-yellow-600'}`} data-id="element-4684">
                        <AlertTriangleIcon className="w-3 h-3 mt-0.5 flex-shrink-0" data-id="element-4685" />
                        {issue.description}
                      </div>)}
                </div>}
              <button className="mt-3 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded transition-colors" data-id="element-4686">
                Verify Bank Details
              </button>
            </div>

            {/* Compliance Documents */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4687">
              <div className="flex items-center gap-2 mb-3" data-id="element-4688">
                <FileTextIcon className="w-4 h-4 text-gray-500" data-id="element-4689" />
                <span className="text-sm font-semibold text-gray-900" data-id="element-4690">
                  Compliance Documents
                </span>
              </div>
              <div className="space-y-2" data-id="element-4691">
                {vendorVerification.documents.map(doc => <div key={doc.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0" data-id="element-4692">
                    <div data-id="element-4693">
                      <div className="text-sm text-gray-900" data-id="element-4694">{doc.name}</div>
                      <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-4695">
                        <span data-id="element-4696">{doc.type}</span>
                        <span data-id="element-4697">•</span>
                        <span data-id="element-4698">Uploaded: {doc.uploadDate}</span>
                        {doc.expiryDate && <>
                            <span data-id="element-4699">•</span>
                            <span className="flex items-center gap-1" data-id="element-4700">
                              <CalendarIcon className="w-3 h-3" data-id="element-4701" />
                              Expires: {doc.expiryDate}
                            </span>
                          </>}
                      </div>
                    </div>
                    {getComplianceStatusBadge(doc.status)}
                  </div>)}
              </div>
            </div>

            {/* Sanctions & Watchlist */}
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4702">
              <div className="flex items-center justify-between mb-3" data-id="element-4703">
                <div className="flex items-center gap-2" data-id="element-4704">
                  <ShieldCheckIcon className="w-4 h-4 text-gray-500" data-id="element-4705" />
                  <span className="text-sm font-semibold text-gray-900" data-id="element-4706">
                    Sanctions & Watchlist
                  </span>
                </div>
                {getComplianceStatusBadge(vendorVerification.sanctionsCheck.status)}
              </div>
              <p className="text-xs text-gray-500" data-id="element-4707">
                Last checked: {vendorVerification.sanctionsCheck.lastChecked}
              </p>
              {vendorVerification.sanctionsCheck.issues.length === 0 && <div className="mt-2 flex items-center gap-1 text-xs text-green-600" data-id="element-4708">
                  <CheckCircleIcon className="w-3 h-3" data-id="element-4709" />
                  No sanctions or watchlist matches found
                </div>}
            </div>
          </div>}
      </div>
    </div>;
}